import { EgenskapTillattVerdi } from '@/domain/vegobjekter/Egenskapstype';
import { Operator, OperatorE } from '@/middleware/queries/Operator';
import {
    AdvancedFilterEgenskapNode,
    AdvancedFilterGroupNode,
    AdvancedFilterNode,
    AdvancedFilterOverlappNode,
    AdvancedFilterRelasjonNode,
} from './AdvancedFilterTypes';

export function compileAdvancedFilter(
    root: AdvancedFilterGroupNode | null,
    excludeNodeId?: string
): string {
    if (!root) return '';
    return compileGroup(root, false, excludeNodeId);
}

function compileGroup(
    node: AdvancedFilterGroupNode,
    needsParens: boolean,
    excludeNodeId?: string
): string {
    const compiledChildren = node.children
        .filter(child => child.id !== excludeNodeId)
        .map(child => compileNode(child, excludeNodeId))
        .filter(compiled => compiled.length > 0);

    if (compiledChildren.length === 0) return '';
    if (compiledChildren.length === 1) {
        return prefixWithNotIfNegated(compiledChildren[0], node.negated);
    }

    const joined = compiledChildren.join(` ${node.operator} `);
    if (node.negated) {
        return `NOT (${joined})`;
    }

    return needsParens ? `(${joined})` : joined;
}

function compileNode(node: AdvancedFilterNode, excludeNodeId?: string): string {
    if (node.id === excludeNodeId) return '';

    switch (node.kind) {
        case 'group':
            return compileGroup(node, true, excludeNodeId);
        case 'egenskap':
            return compileEgenskap(node);
        case 'overlapp':
            return compileOverlapp(node, excludeNodeId);
        case 'relasjon':
            return compileRelasjon(node, excludeNodeId);
    }
}

function compileEgenskap(node: AdvancedFilterEgenskapNode): string {
    if (!node.egenskapstype || node.operator === null) return '';

    const egenskapId = node.egenskapstype.id;
    const operator = Operator.asString(node.operator);

    if (node.operator === OperatorE.HAS_VALUE || node.operator === OperatorE.NO_VALUE) {
        return `egenskap(${egenskapId})${operator}null`;
    }

    if (node.verdi === null) return '';

    return `egenskap(${egenskapId})${operator}${formatValue(node)}`;
}

function formatValue(node: AdvancedFilterEgenskapNode): string {
    if (node.verdi instanceof EgenskapTillattVerdi) {
        return String(node.verdi.id);
    }

    if (node.egenskapstype?.maaQuotes()) {
        return `'${node.verdi}'`;
    }

    return String(node.verdi);
}

function compileOverlapp(
    node: AdvancedFilterOverlappNode,
    excludeNodeId?: string
): string {
    if (node.targetTypeId === null) return '';

    if (!node.child || node.child.children.length === 0) {
        return prefixWithNotIfNegated(`overlapp(${node.targetTypeId})`, node.negated);
    }

    const compiledChild = compileGroup(
        node.child,
        node.child.children.filter(child => child.id !== excludeNodeId).length > 1,
        excludeNodeId
    );

    if (!compiledChild) {
        return prefixWithNotIfNegated(`overlapp(${node.targetTypeId})`, node.negated);
    }

    return prefixWithNotIfNegated(
        `overlapp(${node.targetTypeId}, ${compiledChild})`,
        node.negated
    );
}

function compileRelasjon(
    node: AdvancedFilterRelasjonNode,
    excludeNodeId?: string
): string {
    if (node.targetTypeId === null) return '';

    if (!node.child || node.child.children.length === 0) {
        return prefixWithNotIfNegated(`relasjon(${node.targetTypeId})`, node.negated);
    }

    const compiledChild = compileGroup(
        node.child,
        node.child.children.filter(child => child.id !== excludeNodeId).length > 1,
        excludeNodeId
    );

    if (!compiledChild) {
        return prefixWithNotIfNegated(`relasjon(${node.targetTypeId})`, node.negated);
    }

    return prefixWithNotIfNegated(
        `relasjon(${node.targetTypeId}, ${compiledChild})`,
        node.negated
    );
}

function prefixWithNotIfNegated(compiled: string, negated: boolean): string {
    return negated ? `NOT ${compiled}` : compiled;
}
